import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import { getAllUsers } from "../services/adminService";
import { updateExpiredSubscriptions } from "../services/adminService";

const ActivePlans = () => {
  const navigate = useNavigate();
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: "",
    status: "active",
  });
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchActiveUsers();
  }, [filters]);

  const fetchActiveUsers = async () => {
    try {
      setLoading(true);
      const data = await getAllUsers(filters);

      // Filter only users with subscriptions that are truly active (not expired by date)
      const usersWithActiveSubscriptions = data.users.filter((user) => {
        if (!user.UserSubscriptions || user.UserSubscriptions.length === 0) {
          return false;
        }

        // Filter out expired subscriptions and only keep active ones
        const activeSubscriptions = user.UserSubscriptions.filter(
          (subscription) => {
            // Check if subscription is expired by date
            const isExpiredByDate = isExpired(subscription.currentPeriodEnd);
            // Check if subscription status is active (not expired or canceled)
            const hasActiveStatus = ["active"].includes(subscription.status);

            return hasActiveStatus && !isExpiredByDate;
          }
        );

        // Update the user object to only include active subscriptions
        user.UserSubscriptions = activeSubscriptions;

        // Return true only if user has at least one active subscription
        return activeSubscriptions.length > 0;
      });

      setActiveUsers(usersWithActiveSubscriptions);
      setPagination(data.pagination);
    } catch (error) {
      console.error("Error fetching active users:", error);
      toast.error("Failed to fetch active plans");
    } finally {
      setLoading(false);
    }
  };

  // Calculate days until expiry
  const getDaysUntilExpiry = (endDate) => {
    if (!endDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate calculation

    const expiry = new Date(endDate);
    expiry.setHours(0, 0, 0, 0); // Reset time to start of day

    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Debug logging
    console.log("Date calculation:", {
      today: today.toDateString(),
      expiry: expiry.toDateString(),
      diffDays: diffDays,
      originalEndDate: endDate,
    });

    return diffDays;
  };

  // Validate subscription dates
  const validateSubscriptionDates = (startDate, endDate) => {
    if (!startDate || !endDate)
      return { isValid: false, error: "Missing dates" };

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start >= end) {
      return {
        isValid: false,
        error: "Start date must be before end date",
      };
    }

    return { isValid: true, error: null };
  };

  // Check if plan is expiring soon (within 7 days)
  const isExpiringSoon = (endDate) => {
    if (!endDate) return false;
    const daysLeft = getDaysUntilExpiry(endDate);
    return daysLeft !== null && daysLeft <= 7 && daysLeft > 0;
  };

  // Check if plan is expired
  const isExpired = (endDate) => {
    if (!endDate) return false;
    return getDaysUntilExpiry(endDate) < 0;
  };

  const handleSearch = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleUpdateExpiredSubscriptions = async () => {
    try {
      setLoading(true);
      const result = await updateExpiredSubscriptions();

      // Show success message
      alert(`Updated ${result.updatedCount} expired subscriptions`);

      // Refresh the data
      await fetchActiveUsers();
    } catch (error) {
      console.error("Error updating expired subscriptions:", error);
      alert("Failed to update expired subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateWithTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <PageHeader
          backTo="/admin/dashboard"
          title="Active Subscription Plans"
          subtitle={
            <>
              View all users with currently active (non-expired) subscriptions
              <br />
              <span className="text-sm text-gray-500">
                Note: Expired subscriptions are automatically moved to expired
                status. Use the dashboard to view expired plan counts.
              </span>
            </>
          }
          action={
            <Button
              variant="primary"
              onClick={handleUpdateExpiredSubscriptions}
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {loading ? "Updating..." : "Update Expired Subscriptions"}
            </Button>
          }
        />

        {/* Search */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label
                htmlFor="search"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Search Users
              </label>
              <input
                type="text"
                id="search"
                placeholder="Search by name, email, or plan..."
                value={filters.search}
                onChange={handleSearch}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex items-end">
              <div className="text-sm text-gray-600 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                <span className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-400 rounded-full mr-2"></div>
                  Plans expiring within 7 days are highlighted
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Active Plans Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-12">
              <LoadingSpinner size="lg" text="Loading active plans..." />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Start Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expiry Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Days Left
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {activeUsers.length === 0 ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-6 py-12 text-center text-gray-500"
                        >
                          No currently active (non-expired) subscription plans
                          found
                        </td>
                      </tr>
                    ) : (
                      activeUsers.map((user) =>
                        user.UserSubscriptions.map((subscription) => {
                          const startDate = subscription.currentPeriodStart;
                          const endDate = subscription.currentPeriodEnd;
                          const dateValidation = validateSubscriptionDates(
                            startDate,
                            endDate
                          );
                          const daysLeft = getDaysUntilExpiry(endDate);
                          const expiringSoon = isExpiringSoon(endDate);
                          const expired = isExpired(endDate);

                          // Check for invalid date range
                          const hasInvalidDates = !dateValidation.isValid;

                          return (
                            <tr
                              key={`${user.id}-${subscription.id}`}
                              className={`hover:bg-gray-50 ${
                                hasInvalidDates
                                  ? "bg-red-50 border-l-4 border-l-red-500"
                                  : expiringSoon
                                  ? "bg-yellow-50 border-l-4 border-l-yellow-400"
                                  : expired
                                  ? "bg-red-50 border-l-4 border-l-red-400"
                                  : ""
                              }`}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">
                                      {user.businessName}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {user.email}
                                    </div>
                                    <div className="text-xs text-gray-400">
                                      {user.phone}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  <div className="font-medium">
                                    {subscription.subscriptionPlan?.name ||
                                      "Unknown Plan"}
                                  </div>
                                  <div className="text-gray-500">
                                    $
                                    {subscription.subscriptionPlan?.price ||
                                      "N/A"}
                                    /
                                    {subscription.subscriptionPlan?.interval ||
                                      "N/A"}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    Groups:{" "}
                                    {subscription.subscriptionPlan?.maxGroups ||
                                      "N/A"}{" "}
                                    | Customers:{" "}
                                    {subscription.subscriptionPlan
                                      ?.maxCustomers || "N/A"}{" "}
                                    | Messages:{" "}
                                    {subscription.subscriptionPlan
                                      ?.maxMessages || "N/A"}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div>
                                  {startDate ? formatDate(startDate) : "N/A"}
                                </div>
                                {startDate && (
                                  <div className="text-xs text-gray-400">
                                    {formatDateWithTime(startDate)}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div>
                                  {endDate ? formatDate(endDate) : "N/A"}
                                </div>
                                {endDate && (
                                  <div className="text-xs text-gray-400">
                                    {formatDateWithTime(endDate)}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {daysLeft !== null ? (
                                  <div>
                                    <span
                                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                        expired
                                          ? "bg-red-100 text-red-800"
                                          : expiringSoon
                                          ? "bg-yellow-100 text-yellow-800"
                                          : daysLeft > 30
                                          ? "bg-green-100 text-green-800"
                                          : "bg-blue-100 text-blue-800"
                                      }`}
                                    >
                                      {expired ? "Expired" : `${daysLeft} days`}
                                    </span>
                                    {/* Debug info */}
                                    <div className="text-xs text-gray-400 mt-1">
                                      Raw:{" "}
                                      {endDate
                                        ? new Date(endDate)
                                            .toISOString()
                                            .split("T")[0]
                                        : "No date"}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-gray-400">N/A</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    hasInvalidDates
                                      ? "bg-red-100 text-red-800"
                                      : expired
                                      ? "bg-red-100 text-red-800"
                                      : subscription.status === "active"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {hasInvalidDates
                                    ? "Invalid Dates"
                                    : expired
                                    ? "Expired"
                                    : subscription.status}
                                </span>
                                {hasInvalidDates && (
                                  <div className="text-xs text-red-600 mt-1">
                                    Start date is after end date
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total
                    )}{" "}
                    of {pagination.total} results
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Total Active Plans
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {activeUsers.reduce(
                    (total, user) => total + user.UserSubscriptions.length,
                    0
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-yellow-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L2.732 15.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Expiring Soon
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {activeUsers.reduce((total, user) => {
                    return (
                      total +
                      user.UserSubscriptions.filter((sub) =>
                        isExpiringSoon(sub.currentPeriodEnd)
                      ).length
                    );
                  }, 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Plans expiring within 7 days
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivePlans;
